import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Lightbox from '../components/Lightbox';

export default function References() {
  const [cases, setCases] = useState([]);
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    // Fetch cases and gallery in parallel
    Promise.all([
      fetch('https://api.dev.auxo.dk/public/content/references'),
      fetch('https://api.dev.auxo.dk/public/content/galleries'),
    ])
      .then(([resCases, resGalleries]) => Promise.all([resCases.json(), resGalleries.json()]))
      .then(([casesData, galleriesData]) => {
        setCases(casesData || []);
        const foundGallery = (galleriesData || []).find((g) => g.key === 'referencer');
        setGallery(foundGallery);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching references data:', err);
        setLoading(false);
      });
  }, []);

  return (
    <div className="space-y-20 pb-24">
      {/* Header Section */}
      <section className="relative overflow-hidden bg-brand-gray pt-28 pb-20 border-b border-slate-100">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle, #0f172a 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center relative z-10 space-y-4">
          <h1 className="text-4xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">
            Vores projekter & cases
          </h1>
          <p className="mx-auto max-w-2xl text-lg text-slate-600">
            Vi leverer skræddersyede løsninger med udgangspunkt i hver enkelt kundes ønsker. Se et udvalg af vores projekter her.
          </p>
        </div>
      </section>

      {/* Cases Grid Section */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-brand-accent border-t-transparent" />
          </div>
        ) : (
          <div className="space-y-16">
            {/* Intro text from scrape */}
            <div className="py-4">
              <div className="grid grid-cols-1 gap-8 md:grid-cols-2 md:items-center">
                <div className="space-y-4">
                  <h2 className="text-2xl font-bold text-slate-900">Skræddersyede løsninger</h2>
                  <p className="text-slate-600 leading-relaxed">
                    Vores kunder har alle forskellige behov, og derfor tilbyder vi altid skræddersyede løsninger med udgangspunkt i hver enkelte kundes ønsker. Inden vi går i gang, forventningsafstemmer vi med dig, så vi kan levere et resultat, der lever op til dine forventninger. Se vores projekter og referencer her.
                  </p>
                </div>
                <div className="space-y-4 border-t border-slate-200 pt-6 md:border-t-0 md:border-l md:pt-0 md:pl-8">
                  <h3 className="text-xl font-bold text-slate-900">Kompetent rådgivning</h3>
                  <p className="text-slate-600 leading-relaxed">
                    Er du i tvivl om, hvilken løsning der er den bedste for dig? Det skal du ikke bekymre dig om. Vi tilbyder kompetent rådgivning i forbindelse med ethvert projekt og holder dig opdateret fra start til slut. Du er i trygge hænder hos os.
                  </p>
                </div>
              </div>
            </div>

            {/* Cases list */}
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
              {cases.map((project) => (
                <div
                  key={project.key}
                  className="group flex flex-col justify-between rounded-2xl border border-slate-200 bg-white overflow-hidden transition-all hover:border-brand-accent/30 hover:shadow-md"
                >
                  <div>
                    <div className="relative overflow-hidden aspect-[16/10]">
                      <img
                        src={project.image?.[0] ? `${project.image[0]}?width=600&quality=85&format=auto` : ''}
                        alt={project.title?.da}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-8 space-y-4">
                      <h3 className="text-2xl font-bold text-slate-900 group-hover:text-brand-accent transition-colors">
                        {project.title?.da}
                      </h3>
                      <div
                        className="text-sm text-slate-600 leading-relaxed space-y-3"
                        dangerouslySetInnerHTML={{ __html: project.description?.da }}
                      />
                    </div>
                  </div>
                  <div className="p-8 pt-0">
                    <div className="my-4 border-t border-slate-100" />
                    <Link
                      to="/kontakt"
                      className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-brand-accent hover:underline"
                    >
                      Forespørg lignende opgave
                      <i className="fa-solid fa-arrow-right" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Gallery Section */}
      {gallery && gallery.images && gallery.images.length > 0 && (
        <section className="bg-brand-gray py-16 border-y border-slate-100">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 space-y-10">
            <div className="text-center max-w-2xl mx-auto space-y-3">
              <div className="text-xs font-bold uppercase tracking-widest text-brand-accent">Projektgalleri</div>
              <h2 className="text-3xl font-bold text-slate-900">Billeder fra værkstedet & montage</h2>
              <p className="text-sm text-slate-500">
                Klik på et billede for at se det i fuld størrelse.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {gallery.images.map((img, idx) => (
                <div
                  key={idx}
                  onClick={() => setLightboxIndex(idx)}
                  className="group relative overflow-hidden rounded-xl border border-slate-200 bg-white cursor-pointer aspect-square"
                >
                  <img
                    src={`${img}?width=300&height=300&fit=cover&quality=80&format=auto`}
                    alt={`Referencer galleri ${idx + 1}`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/40 opacity-0 transition-opacity duration-300 group-hover:opacity-100 flex items-center justify-center">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-accent text-white shadow-lg">
                      <i className="fa-solid fa-magnifying-glass-plus" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="relative rounded-2xl border border-slate-200 bg-brand-gray overflow-hidden p-8 sm:p-12 text-center">
          <div className="relative z-10 max-w-2xl mx-auto space-y-6">
            <h2 className="text-3xl font-bold text-slate-900">Har du en specifik opgave?</h2>
            <p className="text-slate-600">
              Vi leverer altid skræddersyede løsninger, der passer præcist til dine krav og specifikationer. Kontakt os i dag for en uforpligtende snak om dit projekt.
            </p>
            <div className="flex flex-wrap justify-center gap-4 pt-4">
              <Link
                to="/kontakt"
                className="rounded-md bg-brand-accent px-6 py-3 text-sm font-semibold uppercase tracking-wider text-white transition-all hover:bg-brand-accent/80"
              >
                Kontakt os i dag
              </Link>
              <a
                href="tel:+4561167778"
                className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-6 py-3 text-sm font-semibold uppercase tracking-wider text-slate-800 transition-all hover:bg-slate-50"
              >
                <i className="fa-solid fa-phone text-brand-accent" />
                <span>+45 61 16 77 78</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxIndex !== null && gallery && (
        <Lightbox
          images={gallery.images}
          currentIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onNavigate={(index) => setLightboxIndex(index)}
        />
      )}
    </div>
  );
}
